import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

import { CalloutBanner } from '@components';
import { useTrans } from '@libs/i18n';

import { useProfileSummary } from '../api';

import { shouldShowCompletionBanner } from './profileNudge.utils';

const ORG_HUB_PATH = '/tenant-profile';

/**
 * Persistent Assessments-Home banner shown only while completion is below 50%.
 * Removal is purely live-recalculated — once the profile reaches 50% the banner
 * stops rendering on every future visit, with no dismiss control (by design).
 */
export const ProfileCompletionBanner: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();
  const navigate = useNavigate();
  const { data } = useProfileSummary();

  if (!data || !shouldShowCompletionBanner(data.progressPct)) return null;

  const pct = data.progressPct;
  const accent = theme.palette.warning.main;

  return (
    <CalloutBanner
      tone="warning"
      density="compact"
      filledIcon
      title={t('app.orgProfile.nudge.banner.title', { percent: pct })}
      sx={{ mb: 2 }}
      trailing={
        <Box
          sx={{
            width: 64,
            height: 4,
            borderRadius: 999,
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: alpha(accent, 0.3),
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${pct}%`,
              borderRadius: 999,
              backgroundColor: accent,
            }}
          />
        </Box>
      }
      action={
        <Button variant="primary" onClick={() => navigate(ORG_HUB_PATH)}>
          {t('app.orgProfile.nudge.banner.cta')}
        </Button>
      }
    >
      {t('app.orgProfile.nudge.banner.body')}
    </CalloutBanner>
  );
});
